import React, { memo } from "react";

import { transformDateToLocalDateString } from "helpers";
import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";

import { VEHICLE_WIZARD_SAMPLE } from "../consts";
import { VehicleWizardSceneProps } from "../types";

const OWNERSHIP_TYPES = ["Vlasništvo", "Leasing", "Najam"];

/** Inicijali za avatar vlasnika (npr. "Orytron d.o.o." → "OD"). */
const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

/** Korak "Vlasnik": vlasnik vozila, vrsta vlasništva i datum kupnje. */
const OwnerScene: React.FC<VehicleWizardSceneProps> = ({ vehicleInfo }) => {
  const ownerName = vehicleInfo.ownerContactName || VEHICLE_WIZARD_SAMPLE.ownerName;
  const ownerType = vehicleInfo.ownerTypeName || VEHICLE_WIZARD_SAMPLE.ownerType;
  const purchaseDate = vehicleInfo.ownerPurchaseDate
    ? transformDateToLocalDateString(new Date(vehicleInfo.ownerPurchaseDate))
    : "15.02.2024.";

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Vlasnik vozila</div>
          <div className="mt-3 flex items-center gap-3">
            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-sm font-bold text-white flex-shrink-0">
              {getInitials(ownerName)}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-bold text-slate-800 truncate">{ownerName}</div>
              <div className="text-[0.65rem] text-slate-400">OIB 12345678901</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-md bg-slate-50 px-2 py-1.5">
              <div className="text-[0.6rem] text-slate-400">Vrsta</div>
              <div className="text-[0.7rem] font-semibold text-slate-700">{ownerType}</div>
            </div>
            <div className="rounded-md bg-slate-50 px-2 py-1.5">
              <div className="text-[0.6rem] text-slate-400">Datum kupnje</div>
              <div className="text-[0.7rem] font-semibold text-slate-700">{purchaseDate}</div>
            </div>
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-xs font-bold text-slate-800">Vrsta vlasništva</div>
          <div className="mt-3 space-y-2">
            {OWNERSHIP_TYPES.map((type) => (
              <div
                key={type}
                className={
                  type === ownerType
                    ? "flex items-center justify-between rounded-md bg-blue-600 px-3 py-2 text-[0.7rem] font-semibold text-white"
                    : "flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-[0.7rem] text-slate-500"
                }
              >
                <span>{type}</span>
                {type === ownerType && <span className="w-2 h-2 rounded-full bg-white" />}
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Vlasnik ide na ugovor"
          subtitle="Kupoprodaja i najam ga preuzimaju"
          iconClassName="bg-emerald-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M9 15l2 2 4-4" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(OwnerScene);
